// The agent answers, then calls addSource (mandatory) as its own step. The tool
// result starts one more step, and a model with nothing left to say fills it by
// restating what it just wrote. Every text block is concatenated on the way to
// the customer, so that restatement shows up as a second paragraph.

// Short blocks ("Yes.", "Done.") repeat legitimately, so they are left alone.
const MIN_COMPARABLE_CHARS = 15

// A genuine follow-on block is longer than this, so buffering only costs a few
// tokens of streaming delay before it flushes and streams normally.
export const REDUNDANT_BLOCK_MAX_CHARS = 200

// The restatement is usually reworded rather than copied ("Confirmed, your
// organization is now on the Plus tier." then "Confirmed: your organization's
// tier is set to Plus."), so word overlap decides it when the text differs.
const RESTATEMENT_WORD_OVERLAP = 0.75

// Below this a shared-word ratio is noise: two four-word sentences overlap by
// chance far more often than two ten-word ones.
const MIN_COMPARABLE_WORDS = 4

const STOP_WORDS = new Set([
	"a",
	"about",
	"after",
	"all",
	"also",
	"and",
	"any",
	"are",
	"as",
	"at",
	"be",
	"been",
	"but",
	"by",
	"can",
	"for",
	"from",
	"has",
	"have",
	"in",
	"is",
	"it",
	"its",
	"not",
	"now",
	"of",
	"on",
	"or",
	"our",
	"so",
	"that",
	"the",
	"their",
	"them",
	"then",
	"there",
	"these",
	"they",
	"this",
	"to",
	"was",
	"were",
	"will",
	"with",
	"you",
	"your",
])

function comparable(html: string): string {
	return html
		.replace(/<[^>]*>/g, " ")
		.replace(/&nbsp;/g, " ")
		.replace(/\s+/g, " ")
		.trim()
		.toLowerCase()
}

function contentWords(text: string): string[] {
	return comparable(text)
		.replace(/[^a-z0-9\s]/g, " ")
		.split(/\s+/)
		.filter((word) => word.length > 2 && !STOP_WORDS.has(word))
}

function isParaphrase(block: string, existing: string): boolean {
	// A long block carries too much of its own to be a closing restatement, and
	// dropping one would cost the customer a real paragraph.
	if (comparable(block).length > REDUNDANT_BLOCK_MAX_CHARS) return false
	const words = contentWords(block)
	if (words.length < MIN_COMPARABLE_WORDS) return false
	const alreadySaid = new Set(contentWords(existing))
	const shared = words.filter((word) => alreadySaid.has(word)).length
	return shared / words.length >= RESTATEMENT_WORD_OVERLAP
}

/** True when `block` says nothing `existing` has not already said. */
export function isRestatement(block: string, existing: string): boolean {
	const candidate = comparable(block)
	if (candidate.length < MIN_COMPARABLE_CHARS) return false
	if (comparable(existing).includes(candidate)) return true
	return isParaphrase(block, existing)
}

/** Joins a turn's text blocks, dropping any that only restate an earlier one. */
export function joinTextBlocks(blocks: string[]): string {
	let joined = ""
	for (const block of blocks) {
		if (!block.trim()) continue
		if (joined && isRestatement(block, joined)) continue
		joined = joined ? `${joined}\n\n${block}` : block
	}
	return joined
}
